import type { StackedProgressBarSegmentVariant } from "@/components/common/stackedProgressBarTypes";
import type {
  IssuanceDashboardCompany,
  IssuanceDashboardDevice,
} from "@/types";

export type SortField = "name" | "country" | "issued" | "expected";
export type SortDirection = "asc" | "desc";

export const computeIssuedPercent = (
  expected: number,
  issued: number
): number => {
  if (expected <= 0) return issued > 0 ? 100 : 0;
  return (issued / expected) * 100;
};

export const formatMwh = (value: number): string => {
  if (!Number.isFinite(value)) return "0";
  return Math.round(value).toLocaleString("en-US");
};

export const formatPercent = (value: number): string => {
  if (!Number.isFinite(value)) return "0";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Muted devices are kept in the device list so operators can manage them, but excluded
// from every aggregate (expected/issued totals, counters, sort keys, summary). This
// matches the spec: "exclude them from statistics/charts, but keep in the device list".
export const isActiveMute = (device: IssuanceDashboardDevice): boolean =>
  device.mute !== null && new Date(device.mute.muted_until) > new Date();

// Display-only: a 0/0 row renders "—" and a neutral progress bar. This is about the data
// shape, not the tier — it applies to muted 0/0 rows too (whose tier is "muted").
export const isEmptyDevice = (device: IssuanceDashboardDevice): boolean =>
  device.expected_mwh === 0 && device.issued_mwh === 0;

// The single per-device classification driving every aggregate and row styling. The
// backend ships the month-data tier (empty/small/scored); mute is the one client-mutable
// dimension (the mute modal edits device.mute in place), so it's overlaid here instead of
// on the wire — a mute takes effect immediately, without a refetch.
export type DeviceTier = "muted" | "empty" | "small" | "scored";
export const deviceTier = (device: IssuanceDashboardDevice): DeviceTier =>
  isActiveMute(device) ? "muted" : device.stat_tier;

// Devices whose MWh counts toward the totals. Small devices ARE included — their MWh is
// truthful and negligible — but kept out of the scored count below.
export const mwhDevices = (
  devices: IssuanceDashboardDevice[]
): IssuanceDashboardDevice[] =>
  devices.filter((d) => {
    const tier = deviceTier(d);
    return tier === "small" || tier === "scored";
  });

// Devices that drive the issued-device count, devices-issued% and the row's progress colour.
// Small devices are excluded: one hobby device missing shouldn't drag the percentage down.
export const scoredDevices = (
  devices: IssuanceDashboardDevice[]
): IssuanceDashboardDevice[] =>
  devices.filter((d) => deviceTier(d) === "scored");

// Small devices shown in the "+N small" breakdowns and tagged in the device list.
export const smallDevices = (
  devices: IssuanceDashboardDevice[]
): IssuanceDashboardDevice[] =>
  devices.filter((d) => deviceTier(d) === "small");

const sumExpected = (devices: IssuanceDashboardDevice[]): number =>
  devices.reduce((acc, d) => acc + d.expected_mwh, 0);

const sumIssued = (devices: IssuanceDashboardDevice[]): number =>
  devices.reduce((acc, d) => acc + d.issued_mwh, 0);

export const companyExpectedMwh = (company: IssuanceDashboardCompany): number =>
  sumExpected(mwhDevices(company.devices));

export const companyIssuedMwh = (company: IssuanceDashboardCompany): number =>
  sumIssued(mwhDevices(company.devices));

// "BELGIUM-BRUGEL" → "Brugel". Last hyphen-separated segment, title-cased.
export const registryShortLabel = (integration: string): string => {
  const parts = integration.split("-");
  const last = parts[parts.length - 1] ?? integration;
  return last.charAt(0) + last.slice(1).toLowerCase();
};

export interface CompanySummary {
  expectedMwh: number;
  issuedMwh: number;
  issuedPercent: number;
  issuedDeviceCount: number;
  eligibleDeviceCount: number;
  devicesIssuedPercent: number;
  smallDeviceCount: number;
  smallIssuedCount: number;
}

export const summarize = (
  companies: IssuanceDashboardCompany[]
): CompanySummary => {
  let expected = 0;
  let issued = 0;
  let issuedDeviceCount = 0;
  let eligibleDeviceCount = 0;
  let smallDeviceCount = 0;
  let smallIssuedCount = 0;
  for (const company of companies) {
    // MWh totals include small devices (truthful, negligible); the device counts are scored
    // on the non-small set; small devices are tallied separately for the "+N small" footnote.
    for (const device of mwhDevices(company.devices)) {
      expected += device.expected_mwh;
      issued += device.issued_mwh;
    }
    for (const device of scoredDevices(company.devices)) {
      eligibleDeviceCount += 1;
      if (device.issued_mwh > 0) issuedDeviceCount += 1;
    }
    for (const device of smallDevices(company.devices)) {
      smallDeviceCount += 1;
      if (device.issued_mwh > 0) smallIssuedCount += 1;
    }
  }
  return {
    expectedMwh: expected,
    issuedMwh: issued,
    issuedPercent: computeIssuedPercent(expected, issued),
    issuedDeviceCount,
    eligibleDeviceCount,
    devicesIssuedPercent:
      eligibleDeviceCount > 0
        ? (issuedDeviceCount / eligibleDeviceCount) * 100
        : 0,
    smallDeviceCount,
    smallIssuedCount,
  };
};

export const filterKeyOf = (
  country: string | null,
  registry: string | null
): string => `${country ?? ""}|${registry ?? ""}`;

export const companyRowKey = (company: IssuanceDashboardCompany): string =>
  `${company.id}-${company.registry}`;

export const sortCompanies = (
  companies: IssuanceDashboardCompany[],
  field: SortField,
  direction: SortDirection
): IssuanceDashboardCompany[] => {
  const factor = direction === "asc" ? 1 : -1;
  const copy = [...companies];
  copy.sort((a, b) => factor * compareCompanies(a, b, field));
  return copy;
};

export const sortedDevices = (
  devices: IssuanceDashboardDevice[]
): IssuanceDashboardDevice[] => {
  return [...devices].sort((a, b) => {
    const percentDelta =
      computeIssuedPercent(a.expected_mwh, a.issued_mwh) -
      computeIssuedPercent(b.expected_mwh, b.issued_mwh);
    if (percentDelta !== 0) return percentDelta;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
};

export const isDeviceMissing = (device: IssuanceDashboardDevice): boolean =>
  device.expected_mwh > 0 && device.issued_mwh === 0;

export const pickVariant = (pct: number): StackedProgressBarSegmentVariant => {
  if (pct >= 95) return "success";
  if (pct >= 75) return "info";
  if (pct >= 50) return "warning";
  return "danger";
};

export const countMissingDevices = (
  devices: IssuanceDashboardDevice[]
): number => devices.filter(isDeviceMissing).length;

export const countIssuedDevices = (
  devices: IssuanceDashboardDevice[]
): number => devices.filter((d) => d.issued_mwh > 0).length;

export const computeDevicesIssuedPercent = (
  devices: IssuanceDashboardDevice[]
): number => {
  const eligible = scoredDevices(devices);
  if (eligible.length === 0) return 0;
  return (countIssuedDevices(eligible) / eligible.length) * 100;
};

const compareCompanies = (
  a: IssuanceDashboardCompany,
  b: IssuanceDashboardCompany,
  field: SortField
): number => {
  if (field === "name") return (a.name ?? "").localeCompare(b.name ?? "");
  if (field === "country")
    return (a.country ?? "").localeCompare(b.country ?? "");
  if (field === "expected")
    return companyExpectedMwh(a) - companyExpectedMwh(b);
  return (
    computeDevicesIssuedPercent(a.devices) -
    computeDevicesIssuedPercent(b.devices)
  );
};
